import { Layers } from "lucide-react"
import { MediaPreview } from "@nexus/ui"
import { formatBrandName } from "@/features/dashboard/lib/format"
import { GCS_BASE, extractString, formatYmd, parseCarouselSlides } from "./drilldown-row"
import { avatarColor } from "@/lib/avatar-color"
import type { ListColumnDef } from "./drilldown-list-columns"

function deriveCompetitorRowInfo(row: Record<string, unknown>) {
  const hash = extractString(row.hash)
  const competitor = extractString(row.competitor)
  const objectType = extractString(row.object_type)
  const carouselSlides = parseCarouselSlides(row, competitor)
  const isCarousel = Boolean(carouselSlides)
  const isVideo = !isCarousel && objectType.toLowerCase() !== "image"
  const ext = isVideo ? "mp4" : "png"
  const thumbnailUrl = hash ? `${GCS_BASE}/${competitor}/thumbnails/${hash}.png` : ""
  const assetUrl = hash ? `${GCS_BASE}/${competitor}/assets/${hash}.${ext}` : ""
  const logoUrl = extractString(row.logo) || null
  return { hash, competitor, objectType, carouselSlides, isCarousel, isVideo, thumbnailUrl, assetUrl, logoUrl }
}

export const COMPETITOR_LEADING_COLUMNS: ListColumnDef[] = [
  {
    key: "creative",
    header: null,
    defaultWidth: 220,
    cellClassName: "overflow-hidden",
    renderCell: (row) => {
      const { competitor, objectType, carouselSlides, isCarousel, isVideo, thumbnailUrl, assetUrl, logoUrl } =
        deriveCompetitorRowInfo(row)
      return (
        <div className="flex h-full min-h-16 w-full items-center gap-2.5">
          <div
            className="group relative size-16 shrink-0"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <MediaPreview
              src={isCarousel ? undefined : assetUrl}
              thumbnailSrc={thumbnailUrl}
              type={isVideo ? "video" : "image"}
              alt={formatBrandName(competitor)}
              title={formatBrandName(competitor)}
              logoSrc={logoUrl ?? undefined}
              slides={carouselSlides ?? undefined}
              className="size-full rounded-lg overflow-hidden cursor-pointer bg-muted"
            />
            {isCarousel ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="flex size-7 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
                  <Layers className="size-3.5" />
                </span>
              </div>
            ) : isVideo ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200 group-hover:opacity-0">
                <span className="flex size-7 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="size-3.5" aria-hidden="true">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </div>
            ) : null}
          </div>
          <p className="text-[11px] capitalize text-muted-foreground">{objectType.toLowerCase()}</p>
        </div>
      )
    },
  },
  {
    key: "brand",
    header: "Brand",
    defaultWidth: 160,
    cellClassName: "overflow-hidden",
    renderCell: (row) => {
      const { competitor, logoUrl } = deriveCompetitorRowInfo(row)
      const color = avatarColor(competitor)
      const initial = competitor.charAt(0).toUpperCase()
      return (
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt={competitor} className="shrink-0 size-6 rounded-full object-cover" />
          ) : (
            <span
              className="shrink-0 size-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white uppercase tracking-wide"
              style={{ backgroundColor: color }}
            >
              {initial}
            </span>
          )}
          <span className="truncate text-xs text-foreground">{competitor ? formatBrandName(competitor) : "—"}</span>
        </div>
      )
    },
  },
]

export const COMPETITOR_TRAILING_COLUMNS: ListColumnDef[] = [
  {
    key: "min_date",
    header: "Min Date",
    defaultWidth: 100,
    cellClassName: "tabular-nums text-xs text-foreground",
    renderCell: (row) => formatYmd(extractString(row.min_date)),
  },
  {
    key: "max_date",
    header: "Max Date",
    defaultWidth: 100,
    cellClassName: "tabular-nums text-xs text-foreground",
    renderCell: (row) => formatYmd(extractString(row.max_date)),
  },
  {
    key: "days",
    header: "Days",
    defaultWidth: 80,
    align: "right",
    cellClassName: "tabular-nums text-right text-xs text-foreground",
    renderCell: (row) => Number(row.Days ?? 0),
  },
  {
    key: "assets",
    header: (
      <span className="inline-flex items-center justify-end gap-1.5">
        <span className="inline-block size-2 rounded-full" style={{ backgroundColor: "#464b67" }} />
        Assets
      </span>
    ),
    defaultWidth: 90,
    align: "right",
    cellClassName: "tabular-nums text-right text-xs text-foreground",
    renderCell: (row) => Number(row.Assets ?? 0),
  },
  {
    key: "days_per_asset",
    header: (
      <span className="inline-flex items-center justify-end gap-1.5">
        <span className="inline-block size-2 rounded-full" style={{ backgroundColor: "#6c4bff" }} />
        Days/Assets
      </span>
    ),
    defaultWidth: 110,
    align: "right",
    cellClassName: "tabular-nums text-right text-xs text-foreground",
    renderCell: (row) => Number(row.DaysPerAsset ?? 0).toFixed(1),
  },
]
